import { useState } from "react";
import { LayoutGrid, Loader2, Plus } from "lucide-react";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { pageActionButtonClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { useAuth } from "@/contexts/AuthContext";
import { createWorkspace } from "../api/rag-api.js";
import { canCreateKmWorkspace } from "../lib/km-org-permissions.js";
import { RagEmptyState } from "../components/RagEmptyState.js";
import { RagPageHeader } from "../components/RagPageHeader.js";
import { CreateWorkspaceDialog } from "../components/CreateWorkspaceDialog.js";
import { useRagWorkspaces } from "../hooks/useRagData.js";
import {
  getLastKmWorkspaceId,
  pickDefaultWorkspaceId,
  workspaceHomePath,
} from "../rag-nav.js";

export function WorkspacesPage() {
  const { user, hasPermission } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const { workspaces, loading, error, reload } = useRagWorkspaces();
  const canCreateWorkspace = canCreateKmWorkspace(hasPermission);

  if (loading) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
        <p className="text-sm text-red-400">{error}</p>
        <Button variant="outline" onClick={() => void reload()}>
          Retry
        </Button>
      </section>
    );
  }

  const defaultWorkspaceId = pickDefaultWorkspaceId(
    workspaces.map((workspace) => ({
      id: workspace.id,
      name: workspace.name,
      description: workspace.description,
      kind: workspace.kind,
      ownerId: workspace.ownerId,
      libraryCount: workspace.libraryCount,
      memberCount: workspace.memberCount,
    })),
    user?.id,
    getLastKmWorkspaceId(),
  );

  if (defaultWorkspaceId) {
    return <Navigate to={workspaceHomePath(defaultWorkspaceId)} replace />;
  }

  return (
    <section className="flex flex-1 flex-col p-6 pb-24 sm:pb-6">
      <RagPageHeader
        icon={LayoutGrid}
        title="Knowledge Management"
        subtitle="Create a workspace to organize libraries and documents"
      />
      <RagEmptyState
        icon={LayoutGrid}
        title="No workspaces yet"
        description="Create your first private workspace to organize documents and start chatting with your knowledge base."
        action={
          canCreateWorkspace ? (
            <Button
              variant="accent"
              onClick={() => setCreateOpen(true)}
              title="Create Workspace"
              aria-label="Create Workspace"
              className={pageActionButtonClassName}
            >
              <Plus className="h-4 w-4" />
              <span className={pageActionLabelClassName}>Create Workspace</span>
            </Button>
          ) : undefined
        }
      />
      <CreateWorkspaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={async (body) => {
          await createWorkspace(body);
          await reload();
        }}
      />
    </section>
  );
}
